import { calcLineTotals } from '../EstimatePanel';
import { activeEstimate, moneyState, remainingBalance, estimateApprovalUrl } from '../../platform/engine/estimateMoney';
import { planTier } from '../../platform/engine/entitlements';

// ─── Lifecycle rail ─────────────────────────────────────────────────────────
// Up to 8 stops spanning both the workflow (intake/diagnosis/repair/qc) and
// the money loop (estimate/approval/invoice/paid) — the two dimensions that,
// per the product architecture doc, are really one job lifecycle. The rail
// is conditional, not fixed: the four money stops render only for a
// customer-pay job on a money-tier shop (see getLifecycleStops).

export const LIFECYCLE_STOPS = [
  { id: 'intake',    label: 'Intake' },
  { id: 'diagnosis', label: 'Diagnosis' },
  { id: 'estimate',  label: 'Estimate' },
  { id: 'approval',  label: 'Approval' },
  { id: 'repair',    label: 'Repair' },
  { id: 'qc',        label: 'QC' },
  { id: 'invoice',   label: 'Invoice' },
  { id: 'paid',      label: 'Paid' },
];

// The three customer-money stops. Skipped for warranty/internal jobs and on
// Flow-tier shops, leaving the 5-stop operational rail (Intake, Diagnosis,
// Repair, QC, Done). The terminal 'paid' stop stays but is relabeled "Done"
// when there's no money loop.
const MONEY_STOP_IDS = ['estimate', 'approval', 'invoice'];

// Does this job run the customer money loop at all? No for warranty/internal
// labor (no customer invoice) and no on a Flow-tier shop (the 5-stop
// operational rail is the whole product there, not a crippled one).
export function isMoneyLoopJob(truck, settings) {
  const lt = String(truck?.laborType || '').toLowerCase();
  if (lt === 'warranty' || lt === 'internal') return false;
  if (truck?.warranty === true) return false; // intake "Warranty Work" checkbox
  if (planTier(settings) === 'flow') return false;
  return true;
}

// The stops that actually render for this job — the money stops are dropped
// when the job isn't on the money loop, and the terminal stop reads "Done"
// instead of "Paid".
export function getLifecycleStops(truck, settings) {
  if (isMoneyLoopJob(truck, settings)) return LIFECYCLE_STOPS;
  return LIFECYCLE_STOPS
    .filter(s => !MONEY_STOP_IDS.includes(s.id))
    .map(s => s.id === 'paid' ? { ...s, label: 'Done' } : s);
}

// The job's current stop as a stable id. Money jobs blend workflow status
// with money stage; operational-only jobs map status straight to the five
// operational stops (the terminal 'paid' id renders as "Done").
function currentStopId(truck, isMoney) {
  const status = truck.status;

  if (!isMoney) {
    if (['done', 'completed'].includes(status)) return 'paid'; // terminal "Done"
    if (['road-test', 'qc'].includes(status)) return 'qc';
    if (status === 'wip') return 'repair';
    if (['triage', 'waiting'].includes(status)) return 'diagnosis';
    return 'intake';
  }

  const { stage } = moneyState(truck);
  if (stage === 'paid')     return 'paid';
  if (stage === 'invoiced') return 'invoice';
  if (status === 'done')    return 'invoice'; // work finished, invoice not generated yet
  if (['road-test', 'qc'].includes(status)) return 'qc';
  if (status === 'wip')     return 'repair';
  if (stage === 'approved') return 'repair'; // approved — repair is the next/current stop
  if (['approval', 'parts-auth', 'parts', 'parts-hold'].includes(status)) return 'approval';
  if (['sent', 'declined'].includes(stage)) return 'approval'; // awaiting or bounced back
  if (stage === 'draft')    return 'estimate';
  if (['triage', 'waiting'].includes(status)) return 'diagnosis';
  return 'intake';
}

// Index of the current stop within the stops that render for this job. If
// the current stop was filtered out, fall back to the nearest earlier stop
// that does render.
export function getLifecycleStopIndex(truck, settings) {
  const stops = getLifecycleStops(truck, settings);
  const id = currentStopId(truck, isMoneyLoopJob(truck, settings));
  const idx = stops.findIndex(s => s.id === id);
  if (idx >= 0) return idx;

  const fullIdx = LIFECYCLE_STOPS.findIndex(s => s.id === id);
  for (let i = fullIdx - 1; i >= 0; i--) {
    const back = stops.findIndex(s => s.id === LIFECYCLE_STOPS[i].id);
    if (back >= 0) return back;
  }
  return 0;
}

// Labor hours sitting on the working estimate, at whatever stage it is in.
// This is the "invoiced" side of clocked-vs-invoiced on the time clock, and it
// prices through calcLineTotals like every other number on the job.
export function estimateLaborHours(truck) {
  const est = activeEstimate(truck);
  if (!est) return 0;
  return (est.lines || []).reduce(
    (sum, ln) => sum + calcLineTotals(ln, truck.parts).laborHours, 0);
}

// Header money numbers: the approved contract value once there is one, and
// hours already quoted/logged that haven't made it onto an invoice yet.
export function getHeaderMoneyNumbers(truck) {
  const est = activeEstimate(truck);
  if (!est) return { approvedAmount: null, unbilledHours: null };

  const { stage, total } = moneyState(truck);
  const approvedAmount = ['approved', 'invoiced', 'paid'].includes(stage) ? total : null;
  const unbilledHours = ['invoiced', 'paid'].includes(stage)
    ? null
    : (est.lines || []).reduce((sum, ln) => sum + calcLineTotals(ln, truck.parts).laborHours, 0);

  return { approvedAmount, unbilledHours: unbilledHours > 0 ? unbilledHours : null };
}

// Money-aware primary CTA. Returns null when the existing workflow-stage
// button (PRIMARY_ACTION in roConstants) should stand as-is; returns an
// action descriptor when the money loop needs to be pushed forward instead.
const REPAIR_STARTED_STATUSES = ['wip', 'road-test', 'qc', 'done'];

export function getNextMoneyAction(truck, settings) {
  if (['completed', 'voided'].includes(truck.status)) return null;
  // Warranty/internal jobs and Flow-tier shops have no money loop — fall back
  // to plain stage advancement (PRIMARY_ACTION handles the CTA).
  if (!isMoneyLoopJob(truck, settings)) return null;
  const { stage } = moneyState(truck);

  if (stage === 'none')  return { label: 'Build Estimate',    kind: 'estimate' };
  if (stage === 'draft') return { label: 'Send for Approval', kind: 'estimate' };

  if (stage === 'approved' && !REPAIR_STARTED_STATUSES.includes(truck.status)) {
    return { label: 'Start Repair →', kind: 'workflow', nextStatus: 'wip' };
  }

  if (truck.status === 'done' && !['invoiced', 'paid'].includes(stage)) {
    return { label: 'Generate Invoice', kind: 'estimate' };
  }

  return null;
}

// ─── Soft money gates ────────────────────────────────────────────────────────
// Skipping a money step never hard-blocks: it asks once and logs the override
// with who/when into the activity stream (the audit trail that feeds the
// cycle-time report). The logged entry doubles as the "asked once" memory —
// if an override for that gate is already on the job, don't ask again.

const GATE_MARKERS = {
  estimate: 'Money gate override — repair started without an approved estimate',
  invoice:  'Money gate override — job closed without an invoice',
};

export function getMoneyGateWarning(truck, nextStatus, settings) {
  if (!isMoneyLoopJob(truck, settings)) return null; // warranty/internal/Flow tier: no money gates
  const { stage } = moneyState(truck);

  if (nextStatus === 'wip' && !['approved', 'invoiced', 'paid'].includes(stage)) {
    return {
      id: 'estimate',
      prompt: `RO ${truck.repairOrder} has no approved estimate — start repair anyway?\n\nThis is allowed, and will be logged on the job.`,
      overrideText: GATE_MARKERS.estimate,
    };
  }

  if (nextStatus === 'completed' && !['invoiced', 'paid'].includes(stage)) {
    return {
      id: 'invoice',
      prompt: `RO ${truck.repairOrder} has not been invoiced — close it anyway?\n\nThis is allowed, and will be logged on the job. It will sit in the Money workspace's Unbilled queue until invoiced.`,
      overrideText: GATE_MARKERS.invoice,
    };
  }

  return null;
}

export function hasGateOverride(truck, gateId) {
  const marker = GATE_MARKERS[gateId];
  if (!marker) return false;
  return (truck.activities || []).some(a => (a?.text || '').startsWith(marker));
}

// ─── Customer text deep links ────────────────────────────────────────────────
// Shared by BlockerBanner and the Comms money-rail card — no SMS API exists
// yet (Twilio integration is a later execution-queue task), so these open
// the phone's own SMS app pre-filled, the same pattern CollectionsView uses
// for payment reminders.

function smsHref(phone, body) {
  const digits = (phone || '').replace(/[^\d+]/g, '');
  if (!digits) return null;
  return `sms:${encodeURIComponent(digits)}?&body=${encodeURIComponent(body)}`;
}

export function buildApprovalSmsHref(truck, est) {
  if (!est?.token) return null;
  const url = estimateApprovalUrl(est.token);
  return smsHref(truck.phone, `RO ${truck.repairOrder}: your estimate is ready to review and approve: ${url}`);
}

export function buildPaymentReminderSmsHref(truck, est) {
  const remaining = remainingBalance(est);
  const link = est?.token ? ` View: ${estimateApprovalUrl(est.token)}` : '';
  return smsHref(truck.phone, `RO ${truck.repairOrder}: invoice balance of $${remaining.toFixed(2)} is outstanding.${link}`);
}
